import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import winSound from '../../assets/audio/winpoints.mp3';
import { LessonService, LessonPayload } from '../../services/lessons/lesson.service';
import { LessonProgressService } from '../../services/lesson-progress/lesson-progress.service';

import { RouterConstant } from '../../routers/index';
import { useLayoutContext } from '../../layouts/general-layout';
import { useUserStore } from '../../utils/userStore';
import { useLessonPlayerStore } from '../../utils/lessonPlayerStore';
import { usePillsStore } from '../../utils/pillsStore';
import { useLessonAutoScroll } from './hooks/useLessonAutoScroll';
import { useLessonAgent } from './hooks/useLessonAgent';
import { useLessonData } from './hooks/useLessonData';
import { useLessonSync } from './hooks/useLessonSync';
import { useLessonEngine } from './hooks/useLessonEngine';
import { useLessonAudio } from './hooks/useLessonAudio';
import { useAudioStore } from '../../utils/audioStore';
import { Interaction, InteractionState } from './types/lesson.types';

export const usePlayLesson = () => {
  const { t, setProgressPercentage, openModesModal } = useLayoutContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { ROUTERS_PATH } = RouterConstant;

  const { levelId, topicId, subTopicId } = location.state || {};

  const {
    lessonData,
    names,
    setNames,
    interactions,
    fetchLesson,
    resolveNames
  } = useLessonData(location.state, topicId, levelId, subTopicId);

  const { levelName, topicName, subTopicName } = names;

  const [loading, setLoading] = useState(true);
  const [currentInteractionIndex, setCurrentInteractionIndex] = useState(-1);
  const [isGreetingDone, setIsGreetingDone] = useState(false);
  const [showInteractions, setShowInteractions] = useState(false);
  const [isVideoEligible, setIsVideoEligible] = useState(false);
  const [isResuming, setIsResuming] = useState(!!location.state?.resume);
  const [showCurtain, setShowCurtain] = useState(isResuming);
  const [activeQuizResponseItemId, setActiveQuizResponseItemId] = useState<string | null>(null);
  const [fadeCurtain, setFadeCurtain] = useState(false);

  // Consolidated interaction state
  const [interactionStates, setInteractionStates] = useState<Record<string, InteractionState>>({});

  const interactionStatesRef = useRef(interactionStates);
  useEffect(() => {
    interactionStatesRef.current = interactionStates;
  }, [interactionStates]);

  useEffect(() => {
    if (currentInteractionIndex < 0) return;
    const item = interactions[currentInteractionIndex];
    if (!item) return;
    setInteractionStates(prev => ({
      ...prev,
      [item.id]: { ...prev[item.id], startedAt: prev[item.id]?.startedAt || Date.now() }
    }));
  }, [currentInteractionIndex, interactions]);
 
  const { user_attributes, userInfo } = useUserStore();
  const { startLesson, updateProgress, clearProgress, syncToDB, currentProgress } = useLessonPlayerStore();
  const { addPoints } = usePillsStore();

  const userInitials = useMemo(() => {
    const first = userInfo?.first_name?.[0] || '';
    const last = userInfo?.last_name?.[0] || '';
    return `${first}${last}`.toUpperCase() || 'U';
  }, [userInfo]);

  const gender = user_attributes?.gender as number | undefined; // 1 for male, 2 for female
  const greetingKey = gender === 1 ? 'playLessonGreeting_male' : 'playLessonGreeting_female';
  const askMeAboutKey = gender === 1 ? 'askMeAbout_male' : (gender === 2 ? 'askMeAbout_female' : 'askMeAbout');

  const fullTopic = (subTopicId && subTopicId !== -1 && subTopicName)
    ? `${topicName} - ${subTopicName}`
    : topicName;

  const scrollContentRef = useRef<HTMLDivElement>(null);

  const {
    agent,
    activeAgentItemId,
    chatThreads,
    setChatThreads,
    handleWantExplanation,
    handleWantClue,
    handleSendFreeText
  } = useLessonAgent({ 
    interactions, 
    currentInteractionIndex, 
    interactionStates, 
    interactionStatesRef, 
    setInteractionStates, 
    lessonData, 
    fullTopic, 
    setIsResuming, 
    t, 
    gender 
  });

  const { debouncedSync, getFilteredStates, handleResumeProgress } = useLessonSync({
    lessonData,
    interactionStates,
    interactionStatesRef,
    setInteractionStates,
    setNames,
    totalItems: interactions.length,
    interactions,
    currentInteractionIndex
  });

  const { handleNextInteraction, handleGreetingComplete, handleQuizAnswer } = useLessonEngine({
    interactions,
    currentInteractionIndex,
    setCurrentInteractionIndex,
    interactionStates,
    interactionStatesRef,
    setInteractionStates,
    chatThreads,
    lessonData,
    totalItems: interactions.length,
    setIsResuming,
    setIsGreetingDone,
    setShowInteractions,
    updateProgress,
    debouncedSync,
    syncToDB,
    getFilteredStates,
    navigate,
    ROUTERS_PATH,
    fullTopic,
    levelName,
    currentProgress
  });

  useLessonAutoScroll({ scrollContentRef, currentInteractionIndex, isGreetingDone, loading, interactions, showInteractions, interactionStates, chatThreads, agentIsLoading: agent.isLoading, showCurtain, activeQuizResponseItemId });

  const { playForInteraction, prefetchInteraction, replayForInteraction, togglePauseForInteraction, stopAudio: stopLessonAudio } = useLessonAudio({ 
    isResuming, 
    lessonId: lessonData?.id 
  });

  // Clean up audio cache when leaving the lesson to free memory
  // and prevent cache collision bugs across different lessons.
  useEffect(() => {
    return () => {
      useAudioStore.getState().clearAudioCache();
    };
  }, []);

  // Reset isResuming to false as soon as active interaction steps begin so TTS audio plays cleanly
  useEffect(() => {
    if (currentInteractionIndex >= 0 && isGreetingDone) {
      setIsResuming(false);
    }
  }, [currentInteractionIndex, isGreetingDone, setIsResuming]);

  // Automatically pre-fetch audio for upcoming interaction steps in the background
  // Pre-fetches step 0 & step 1 during greeting animation so the 1st step audio is 0s instant!
  useEffect(() => {
    if (interactions.length > 0) {
      if (currentInteractionIndex <= 0) {
        prefetchInteraction(interactions[0]);
        if (interactions[1]) {
          prefetchInteraction(interactions[1]);
        }
      } else {
        const nextInteraction = interactions[currentInteractionIndex + 1];
        if (nextInteraction) {
          prefetchInteraction(nextInteraction);
        }
      }
    }
  }, [currentInteractionIndex, interactions, prefetchInteraction]);

  useEffect(() => {
    const { lessonId: stateLessonId } = location.state || {};
    if (!stateLessonId && (!levelId || !topicId)) {
      navigate(ROUTERS_PATH.SEARCH_LESSON);
      return;
    }

    /** Clears all UI state for a fresh lesson load */
    const resetUIState = () => {
      setChatThreads({});
      setInteractionStates({});
      setCurrentInteractionIndex(-1);
      setIsGreetingDone(false);
      setShowInteractions(false);
    };

    // --- Orchestrator ---
    const loadAndStartLesson = async () => {
      try {
        setLoading(true);
        resetUIState();

        const { data, lessonId, effectiveTopicId, effectiveLevelId, effectiveSubTopicId } = await fetchLesson();
        const { resume, from } = location.state || {};

        if (resume && lessonId) {
          const progress = await handleResumeProgress(lessonId, data, effectiveTopicId, effectiveLevelId, effectiveSubTopicId);
          if (progress && (progress.percentage >= 100 || progress.status === 2)) {
            if (from === 'smartCatalog') {
              clearProgress(effectiveTopicId, effectiveLevelId, effectiveSubTopicId);
              interactionStatesRef.current = {};
              setInteractionStates({});
              setIsResuming(false);
              setShowCurtain(false);
            } else {
              const lName = names.levelName || progress.level_name || '';
              const tName = names.topicName || progress.full_topic_label || '';
              navigate(ROUTERS_PATH.LESSON_SUMMARY, {
                state: {
                  fullTopic: (effectiveSubTopicId && effectiveSubTopicId !== -1 && names.subTopicName) ? `${tName} - ${names.subTopicName}` : tName,
                  levelName: lName,
                  lessonId,
                  isFinalSummary: true,
                  lessonData: data,
                  optimisticStats: {
                    pointsEarned: 0,
                    accuracy: 100,
                    completedCount: progress.completed_interaction_ids?.length || 0,
                    totalCount: progress.total_interactions || 0
                  }
                }
              });
              return;
            }
          } else if (!progress || !progress.completed_interaction_ids || progress.completed_interaction_ids.length === 0) {
            setIsResuming(false);
            setShowCurtain(false);
          }
        } else {
          clearProgress(effectiveTopicId, effectiveLevelId, effectiveSubTopicId);
        }

        await resolveNames(effectiveLevelId, effectiveTopicId, effectiveSubTopicId);
        startLesson(effectiveTopicId, effectiveLevelId, effectiveSubTopicId, fullTopic, names.levelName, names.topicName);
      } catch (error) {
        console.error("Failed to load lesson", error);
      } finally {
        setLoading(false);
      }
    };

    loadAndStartLesson();
  }, [levelId, topicId, subTopicId, navigate, ROUTERS_PATH.SEARCH_LESSON, startLesson, clearProgress, location.state, fetchLesson, resolveNames, handleResumeProgress]);


  // Reset video eligibility on index change (typing flags are now per-item, no reset needed)
  useEffect(() => {
    setIsVideoEligible(false);
  }, [currentInteractionIndex]);

  // Calculate Progress
  const totalItems = interactions.length;
  const currentItemsDone = Math.max(0, currentInteractionIndex);
  const progressPercentageVal = totalItems > 0 ? Math.round((currentItemsDone / totalItems) * 100) : 0;

  useEffect(() => {
    if (lessonData && isGreetingDone) {
      setProgressPercentage(progressPercentageVal);
    }
    return () => setProgressPercentage(undefined);
  }, [progressPercentageVal, setProgressPercentage, lessonData, isGreetingDone]);

  /** Single setter for per-item typing completion — replaces 4 global boolean setters */
  const markTypingDone = useCallback((itemId: string, field: 'isTitleDone' | 'isContentDone' | 'isChatDone') => {
    setInteractionStates(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: true }
    }));
  }, []);

  // Derived arrays from interactionStates
  const rewardedIds = useMemo(() => Object.entries(interactionStates).filter(([_, s]) => s.isRewarded).map(([id]) => id), [interactionStates]);
  const correctAnswerIds = useMemo(() => Object.entries(interactionStates).filter(([_, s]) => s.isCorrect).map(([id]) => id), [interactionStates]);
  const successMessageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(interactionStates).forEach(([id, s]) => {
      if (s.successMessageCount !== undefined) counts[id] = s.successMessageCount;
    });
    return counts;
  }, [interactionStates]);

  return {
    t,
    loading,
    lessonData,
    interactions,
    currentInteractionIndex,
    isGreetingDone,
    showInteractions,
    rewardedIds,
    isAgentLoading: agent.isLoading,
    activeAgentItemId,
    greetingKey,
    askMeAboutKey,
    fullTopic,
    levelName,
    topicName,
    scrollContentRef,
    handleNextInteraction,
    handleWantExplanation,
    handleWantClue,
    handleGreetingComplete,
    markTypingDone,
    openModesModal,
    navigate,
    ROUTERS_PATH,
    correctAnswerIds,
    handleQuizAnswer,
    handleSendFreeText,
    userInitials,
    userInfo,
    chatThreads,
    interactionStates,
    successMessageCounts,
    isVideoEligible,
    setIsVideoEligible,
    isResuming,
    gender,
    showCurtain,
    setShowCurtain,
    fadeCurtain,
    setFadeCurtain,
    activeQuizResponseItemId,
    setActiveQuizResponseItemId,
    playForInteraction,
    replayForInteraction,
    togglePauseForInteraction,
    stopLessonAudio
  };
};
// Agent synced
